import { Dayjs } from 'dayjs';
import { dayjs, ET } from 'util/shared/date';
import { SearchHit, SNIPPET_POST, SNIPPET_PRE } from 'types/search';

export type TimeFilter = 'all' | 'am' | 'pm';

// One definition of the AM/PM split for both lists: the browse list holds
// dayjs dates, search hits carry the raw ISO string.
export const matchesTimeFilter = (
  date: string | Dayjs | null | undefined,
  filter: TimeFilter,
) => {
  if (filter === 'all') {
    return true;
  }
  if (date == null) {
    return false;
  }
  const etHour = dayjs(date).tz(ET).hour();
  return filter === 'am' ? etHour < 12 : etHour >= 12;
};

// One document has three key shapes: the browse list and a search hit both
// carry '<category>/<id>', while the route carries the bare id. Anything that
// compares two of them has to normalise first, or the comparison silently
// misses and the caller reads it as 'no such document'.
//
// Drops the leading category segment only. Ids are not always flat, since the
// WordPress-era notes are keyed 'founders-notes/wordpress/<id>', so this
// keeps everything after the first '/' and leaves an already-bare id alone.
// Anchored, so a category name appearing later in a key is not touched.
export const contentIdOf = (key: string) => key.replace(/^[^/]+\//, '');

export interface SnippetSegment {
  text: string;
  marked: boolean;
}

// Sentinel-delimited fragment -> segments. Marked segments are what the
// highlighter matched, in the document's own spelling. The delimiters always
// come in order, so splitting on PRE and then on POST recovers the structure
// without a parser.
export const splitSnippet = (fragment: string): SnippetSegment[] => {
  const segments: SnippetSegment[] = [];
  for (const chunk of fragment.split(SNIPPET_PRE)) {
    const [marked, ...rest] = chunk.split(SNIPPET_POST);
    // The first chunk has no opening sentinel, so it is plain text; every
    // later chunk opens with the matched term and continues with plain text.
    if (segments.length === 0 && rest.length === 0) {
      if (marked !== '') {
        segments.push({ text: marked, marked: false });
      }
      continue;
    }
    if (marked !== '') {
      segments.push({ text: marked, marked: true });
    }
    const tail = rest.join(SNIPPET_POST);
    if (tail !== '') {
      segments.push({ text: tail, marked: false });
    }
  }
  return segments;
};

// The distinct surface forms a hit matched on, longest first so 'zero gamma'
// is tried before 'gamma' when scanning a document. Case-folded for dedup; the
// original casing is irrelevant because matching is case-insensitive.
//
// Derived from the fragments rather than from a second _analyze call: the
// analyzer emits stems ('flip', not 'flipping'), which do not string-match
// document text. See spec 4.6.
export const matchedTerms = (snippets: string[]): string[] => {
  const seen = new Map<string, string>();
  for (const fragment of snippets) {
    for (const segment of splitSnippet(fragment)) {
      if (!segment.marked) {
        continue;
      }
      const term = segment.text.trim();
      if (term === '') {
        continue;
      }
      const folded = term.toLowerCase();
      if (!seen.has(folded)) {
        seen.set(folded, folded);
      }
    }
  }
  return [...seen.values()].sort((a, b) => b.length - a.length);
};

// A matched surface form can hold '(', '+' or '.', because 'S&P 500 (SPX)' is
// one term, so every term is escaped before it reaches a pattern.
const escapeRegExp = (term: string) =>
  term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const WORD_CHAR = /[A-Za-z0-9]/;

// A term must not match starting or ending mid-token: the corpus vocabulary is
// full of short forms like 'IV', 'vol', 'VT' and 'SPX', and unguarded they light
// up inside 'derivative', 'volatility' and 'involved', which makes the whole
// feature look broken.
//
// Explicit classes rather than \b, for two reasons: \b is defined against \w,
// which counts '_', and it is only meaningful next to a word character. Terms
// here can begin or end on punctuation ('S&P 500 (SPX)'), where a boundary
// assertion would test the wrong edge, so each side is guarded only when that
// side actually ends on a word character.
const wordBounded = (term: string) => {
  const lead = WORD_CHAR.test(term[0]) ? '(?<![A-Za-z0-9])' : '';
  const tail = WORD_CHAR.test(term[term.length - 1]) ? '(?![A-Za-z0-9])' : '';
  return `${lead}${escapeRegExp(term)}${tail}`;
};

// Case-insensitive, word-bounded, longest-term-first, non-overlapping. Terms
// come from matchedTerms(), so they are already the document's surface forms
// rather than analyzer stems. The alternation is ordered longest-first because a
// JS regex takes the first branch that matches at a position: that is what stops
// 'gamma' from marking inside 'zero gamma'.
//
// Because a term only marks on whole tokens, a morphological variant is marked
// only when the highlighter actually returned it. Searching 'flip' marks
// 'flipped' only if some fragment carried 'flipped'. That is the deliberate
// trade for not marking inside unrelated words.
export const markSegments = (
  text: string,
  terms: string[],
): SnippetSegment[] => {
  if (text === '') {
    return [];
  }
  const usable = terms.filter((term) => term !== '');
  if (usable.length === 0) {
    return [{ text, marked: false }];
  }
  const pattern = new RegExp(
    [...usable]
      .sort((a, b) => b.length - a.length)
      .map(wordBounded)
      .join('|'),
    'gi',
  );
  const segments: SnippetSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), marked: false });
    }
    segments.push({ text: match[0], marked: true });
    cursor = start + match[0].length;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), marked: false });
  }
  return segments;
};

// How much unmarked text may sit ahead of the first mark in a rendered
// fragment. The sidebar is ~320px and clamps to three lines, so only the first
// ~140 characters of a row survive; a 160-character fragment whose match landed
// late is clipped away entirely and the row reads as if it matched nothing.
// Roughly one line's worth, which keeps enough left context to read.
export const SNIPPET_LEAD_BUDGET = 48;

// Pull the first mark up to the front of a fragment, dropping the excess lead.
// The highlighter centres a fragment on its match, so the lead is ordinary
// sentence text with no claim on the reader's attention. The match is the
// thing the row exists to show.
export const trimSnippetLead = (
  segments: SnippetSegment[],
  budget: number = SNIPPET_LEAD_BUDGET,
): SnippetSegment[] => {
  const firstMark = segments.findIndex((segment) => segment.marked);
  // No mark at all, or it already leads: nothing to pull forward.
  if (firstMark <= 0) {
    return segments;
  }
  const lead = segments
    .slice(0, firstMark)
    .map((segment) => segment.text)
    .join('');
  if (lead.length <= budget) {
    return segments;
  }
  // Cut at a word boundary so the ellipsis does not land mid-word. No space in
  // the kept tail means one very long token, and slicing it is the best on offer.
  const tail = lead.slice(lead.length - budget);
  const firstSpace = tail.indexOf(' ');
  const kept = firstSpace === -1 ? tail : tail.slice(firstSpace + 1);
  return [{ text: `…${kept}`, marked: false }, ...segments.slice(firstMark)];
};

// Search rows carry the three fields ReportsList renders. dateDisplayString is
// derived here exactly as parseNoteJson does for the browse list.
export const searchHitToReportItem = (hit: SearchHit, timezone: string) => ({
  key: hit.key,
  title: hit.title,
  dateDisplayString:
    hit.date == null
      ? ''
      : dayjs(hit.date)
          .tz(timezone)
          .format('dddd, MMMM DD, YYYY [at] h:mm a z'),
  // Carried through so the row can render them; browse rows have none.
  snippets: hit.snippets,
});
